import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Text,
    Button,
    Badge,
    Dropdown,
    Option,
    Spinner,
    Tab,
    TabList,
    DataGrid,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridBody,
    DataGridRow,
    DataGridCell,
    ProgressBar,
    Divider,
    Tooltip,
} from '@fluentui/react-components';
import {
    ChartMultiple24Regular,
    People24Regular,
    Clock24Regular,
    Calendar24Regular,
    DataTrending24Regular,
    Warning24Regular,
    CheckmarkCircle24Regular,
    Dismiss24Regular,
    VideoClip24Regular,
    DocumentPdf24Regular,
    ArrowDownload24Regular,
    Fire24Regular,
    Info24Regular,
} from '@fluentui/react-icons';
import { apiService } from '../../../services/api/apiService';
import { socketService } from '../../../services/socket/socketService';

interface AnalyticsData {
    timeframe: string;
    dateRange: {
        start: string;
        end: string;
    };
    consultationMetrics: {
        total: number;
        completed: number;
        cancelled: number;
        emergency: number;
        inProgress: number;
        successRate: number;
        cancellationRate: number;
        scheduled: number;
    };
    doctorUtilization: {
        summary: {
            totalDoctors: number;
            activeDoctors: number;
            doctorsInSession: number;
            avgUtilization: number;
            utilizationRate: number;
        };
        doctors: DoctorUtilization[];
    };
    durationAnalytics: {
        avgDurationMinutes: number;
        medianDurationMinutes: number;
        shortestDurationMinutes: number;
        longestDurationMinutes: number;
        byType: Record<string, { count: number; avgDuration: number }>;
        byPriority: Record<string, { count: number; avgDuration: number }>;
        distribution: { range: string; count: number }[];
    };
    statusDistribution: {
        distribution: Record<string, { count: number; percentage: string }>;
        total: number;
    };
    trends: {
        groupBy: string;
        data: TrendData[];
    };
    generatedAt: string;
}

interface DoctorUtilization {
    doctorId: number;
    name: string;
    specialty: string;
    totalConsultations: number;
    completedConsultations: number;
    completionRate: string;
    avgDurationMinutes: number;
    currentlyInSession: boolean;
    utilizationScore: number;
}

interface TrendData {
    period: string;
    total: number;
    completed: number;
    cancelled: number;
    emergency: number;
}

interface RealTimeMetrics {
    activeConsultations: number;
    todayMetrics: {
        total: number;
        completed: number;
    };
    waitingPatients: number;
    availableDoctors: number;
    timestamp: string;
}

export const AnalyticsDashboard: React.FC = () => {
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [realTimeMetrics, setRealTimeMetrics] = useState<RealTimeMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [timeframe, setTimeframe] = useState<string>('month');
    const [activeTab, setActiveTab] = useState<string>('overview');
    const [lastUpdated, setLastUpdated] = useState<string>('');
    const [refreshing, setRefreshing] = useState(false);

    const timeframeOptions = [
        { value: 'week', label: 'Last Week' },
        { value: 'month', label: 'This Month' },
        { value: 'quarter', label: 'This Quarter' },
        { value: 'year', label: 'This Year' },
    ];

    const loadAnalyticsData = useCallback(
        async (selectedTimeframe: string = timeframe) => {
            try {
                setRefreshing(true);
                console.log(`📊 Loading analytics data for timeframe: ${selectedTimeframe}`);

                const [dashboardData, realTimeData] = await Promise.all([
                    apiService.getAnalyticsDashboard(selectedTimeframe),
                    apiService.getRealTimeMetrics(),
                ]);

                setAnalyticsData(dashboardData);
                setRealTimeMetrics(realTimeData);
                setLastUpdated(new Date().toLocaleTimeString());
            } catch (error) {
                console.error('❌ Error loading analytics data:', error);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [timeframe]
    );

    useEffect(() => {
        loadAnalyticsData();

        // Set up real-time updates for metrics via polling
        const interval = setInterval(() => {
            if (activeTab === 'overview') {
                apiService.getRealTimeMetrics().then(setRealTimeMetrics).catch(console.error);
            }
        }, 30000); // Update every 30 seconds

        // Set up socket.io real-time analytics updates
        const handleAnalyticsUpdate = (data: any) => {
            console.log('📊 Real-time analytics update received:', data.eventType);
            setRealTimeMetrics(data.metrics);
            setLastUpdated(new Date().toLocaleTimeString());
        };

        socketService.on('analyticsUpdate', handleAnalyticsUpdate);

        return () => {
            clearInterval(interval);
            socketService.off('analyticsUpdate', handleAnalyticsUpdate);
        };
    }, [loadAnalyticsData, activeTab]);

    const handleTimeframeChange = (value: string) => {
        setTimeframe(value);
        loadAnalyticsData(value);
    };

    const handleRefresh = () => {
        loadAnalyticsData();
    };

    const handleExport = async (format: 'json' | 'csv') => {
        try {
            console.log(`📁 Exporting analytics data as ${format.toUpperCase()}`);
            await apiService.exportAnalytics(timeframe, format);
        } catch (error) {
            console.error('❌ Error exporting analytics:', error);
        }
    };

    const renderOverviewTab = () => {
        if (!analyticsData || !realTimeMetrics) return null;

        const cm = analyticsData.consultationMetrics;
        const du = analyticsData.doctorUtilization.summary;
        const da = analyticsData.durationAnalytics;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Real-time Metrics Row */}
                <div
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}
                >
                    <Card style={{ padding: '20px', border: '2px solid #0078d4' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <VideoClip24Regular style={{ fontSize: '32px', color: '#0078d4' }} />
                            <div>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Active Now
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {realTimeMetrics.activeConsultations}
                                </Text>
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Calendar24Regular style={{ fontSize: '32px', color: '#107c10' }} />
                            <div>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Today's Total
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {realTimeMetrics.todayMetrics.total}
                                </Text>
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <People24Regular style={{ fontSize: '32px', color: '#8764b8' }} />
                            <div>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Doctors Available
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {realTimeMetrics.availableDoctors}
                                </Text>
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Clock24Regular style={{ fontSize: '32px', color: '#d83b01' }} />
                            <div>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Waiting Patients
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {realTimeMetrics.waitingPatients}
                                </Text>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Main Metrics Row */}
                <div
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}
                >
                    <Card style={{ padding: '24px' }}>
                        <Text size={500} weight="bold" style={{ marginBottom: '16px' }}>
                            📊 Consultation Volume
                        </Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Total Consultations</Text>
                                <Text weight="bold">{cm.total.toLocaleString()}</Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Completed</Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CheckmarkCircle24Regular style={{ color: '#107c10' }} />
                                    <Text weight="bold" style={{ color: '#107c10' }}>
                                        {cm.completed}
                                    </Text>
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Emergency</Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Warning24Regular style={{ color: '#d83b01' }} />
                                    <Text weight="bold" style={{ color: '#d83b01' }}>
                                        {cm.emergency}
                                    </Text>
                                </div>
                            </div>
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Success Rate</Text>
                                <Badge appearance="tint" color="brand">
                                    {cm.successRate}%
                                </Badge>
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '24px' }}>
                        <Text size={500} weight="bold" style={{ marginBottom: '16px' }}>
                            👩‍⚕️ Doctor Utilization
                        </Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Total Doctors</Text>
                                <Text weight="bold">{du.totalDoctors}</Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Active Doctors</Text>
                                <Text weight="bold" style={{ color: '#107c10' }}>
                                    {du.activeDoctors}
                                </Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>In Session</Text>
                                <Badge appearance="tint" color="danger">
                                    {du.doctorsInSession}
                                </Badge>
                            </div>
                            <Divider />
                            <div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '8px',
                                    }}
                                >
                                    <Text>Utilization Rate</Text>
                                    <Text weight="bold">{du.utilizationRate}%</Text>
                                </div>
                                <ProgressBar value={du.utilizationRate} max={100} />
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '24px' }}>
                        <Text size={500} weight="bold" style={{ marginBottom: '16px' }}>
                            ⏱️ Duration Analytics
                        </Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Average Duration</Text>
                                <Text weight="bold">{da.avgDurationMinutes} min</Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Median Duration</Text>
                                <Text weight="bold">{da.medianDurationMinutes} min</Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Shortest</Text>
                                <Text style={{ color: '#107c10' }}>
                                    {da.shortestDurationMinutes} min
                                </Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>Longest</Text>
                                <Text style={{ color: '#d83b01' }}>
                                    {da.longestDurationMinutes} min
                                </Text>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Duration Distribution Chart */}
                <Card style={{ padding: '24px' }}>
                    <Text size={500} weight="bold" style={{ marginBottom: '16px' }}>
                        📈 Duration Distribution
                    </Text>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: '16px',
                        }}
                    >
                        {da.distribution.map((bucket, index) => (
                            <div key={index} style={{ textAlign: 'center' }}>
                                <div
                                    style={{
                                        height: `${Math.max(bucket.count * 10, 20)}px`,
                                        backgroundColor: '#0078d4',
                                        marginBottom: '8px',
                                        borderRadius: '4px',
                                        transition: 'all 0.3s ease',
                                    }}
                                />
                                <Text size={200}>{bucket.range}</Text>
                                <Text size={300} weight="bold" style={{ display: 'block' }}>
                                    {bucket.count}
                                </Text>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        );
    };

    const renderDoctorsTab = () => {
        if (!analyticsData) return null;

        const doctors = analyticsData.doctorUtilization.doctors;

        return (
            <Card style={{ padding: '24px' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px',
                    }}
                >
                    <Text size={600} weight="bold">
                        👩‍⚕️ Doctor Performance & Utilization
                    </Text>
                    <Badge appearance="tint" color="brand">
                        {doctors.length} Doctors
                    </Badge>
                </div>

                <DataGrid
                    items={doctors}
                    columns={[
                        {
                            columnId: 'name',
                            renderHeaderCell: () => 'Doctor',
                            renderCell: (doctor: any) => <Text weight="bold">{doctor.name}</Text>,
                            compare: (a: any, b: any) => a.name.localeCompare(b.name),
                        },
                        {
                            columnId: 'specialty',
                            renderHeaderCell: () => 'Specialty',
                            renderCell: (doctor: any) => (
                                <Badge appearance="outline">{doctor.specialty}</Badge>
                            ),
                            compare: (a: any, b: any) => a.specialty.localeCompare(b.specialty),
                        },
                        {
                            columnId: 'consultations',
                            renderHeaderCell: () => 'Consultations',
                            renderCell: (doctor: any) => <Text>{doctor.totalConsultations}</Text>,
                            compare: (a: any, b: any) =>
                                a.totalConsultations - b.totalConsultations,
                        },
                        {
                            columnId: 'completion',
                            renderHeaderCell: () => 'Completion Rate',
                            renderCell: (doctor: any) => (
                                <Badge
                                    appearance="tint"
                                    color={
                                        parseFloat(doctor.completionRate) > 90
                                            ? 'success'
                                            : parseFloat(doctor.completionRate) > 75
                                            ? 'warning'
                                            : 'danger'
                                    }
                                >
                                    {doctor.completionRate}%
                                </Badge>
                            ),
                            compare: (a: any, b: any) =>
                                parseFloat(a.completionRate) - parseFloat(b.completionRate),
                        },
                        {
                            columnId: 'duration',
                            renderHeaderCell: () => 'Avg Duration',
                            renderCell: (doctor: any) => (
                                <Text>{doctor.avgDurationMinutes} min</Text>
                            ),
                            compare: (a: any, b: any) =>
                                a.avgDurationMinutes - b.avgDurationMinutes,
                        },
                        {
                            columnId: 'status',
                            renderHeaderCell: () => 'Status',
                            renderCell: (doctor: any) =>
                                doctor.currentlyInSession ? (
                                    <Badge appearance="tint" color="danger">
                                        In Session
                                    </Badge>
                                ) : (
                                    <Badge appearance="tint" color="success">
                                        Available
                                    </Badge>
                                ),
                            compare: (a: any, b: any) =>
                                (a.currentlyInSession ? 1 : 0) - (b.currentlyInSession ? 1 : 0),
                        },
                        {
                            columnId: 'utilization',
                            renderHeaderCell: () => 'Utilization',
                            renderCell: (doctor: any) => (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <ProgressBar
                                        value={doctor.utilizationScore.toString()}
                                        max={Math.max(
                                            ...doctors.map((d: any) => d.utilizationScore)
                                        )}
                                        style={{ width: '100px' }}
                                    />
                                    <Text size={200}>{doctor.utilizationScore}</Text>
                                </div>
                            ),
                            compare: (a: any, b: any) => a.utilizationScore - b.utilizationScore,
                        },
                    ]}
                />
            </Card>
        );
    };

    const renderTrendsTab = () => {
        if (!analyticsData) return null;

        const trends = analyticsData.trends.data;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <Card style={{ padding: '24px' }}>
                    <Text size={600} weight="bold" style={{ marginBottom: '16px' }}>
                        📈 Consultation Trends
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {trends.map((trend, index) => (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                }}
                            >
                                <div style={{ minWidth: '100px' }}>
                                    <Text weight="bold">{trend.period}</Text>
                                </div>
                                <div style={{ flex: 1, display: 'flex', gap: '16px' }}>
                                    <div>
                                        <Text size={200}>Total: </Text>
                                        <Text weight="bold">{trend.total}</Text>
                                    </div>
                                    <div>
                                        <Text size={200}>Completed: </Text>
                                        <Text weight="bold" style={{ color: '#107c10' }}>
                                            {trend.completed}
                                        </Text>
                                    </div>
                                    <div>
                                        <Text size={200}>Cancelled: </Text>
                                        <Text weight="bold" style={{ color: '#d83b01' }}>
                                            {trend.cancelled}
                                        </Text>
                                    </div>
                                    <div>
                                        <Text size={200}>Emergency: </Text>
                                        <Text weight="bold" style={{ color: '#ff8c00' }}>
                                            {trend.emergency}
                                        </Text>
                                    </div>
                                </div>
                                <div style={{ width: '200px' }}>
                                    <ProgressBar
                                        value={trend.completed}
                                        max={trend.total}
                                        color="success"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card style={{ padding: '24px' }}>
                    <Text size={500} weight="bold" style={{ marginBottom: '16px' }}>
                        📊 Status Distribution
                    </Text>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px',
                        }}
                    >
                        {Object.entries(analyticsData.statusDistribution.distribution).map(
                            ([status, data]) => (
                                <div
                                    key={status}
                                    style={{
                                        padding: '16px',
                                        border: '1px solid #e1e5e9',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Text
                                        weight="bold"
                                        style={{
                                            textTransform: 'capitalize',
                                            display: 'block',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        {status.replace('_', ' ')}
                                    </Text>
                                    <Text size={700} weight="bold" style={{ display: 'block' }}>
                                        {data.count}
                                    </Text>
                                    <Text size={300} style={{ color: '#605e5c' }}>
                                        {data.percentage}%
                                    </Text>
                                </div>
                            )
                        )}
                    </div>
                </Card>
            </div>
        );
    };

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '400px',
                    gap: '16px',
                }}
            >
                <Spinner size="large" />
                <Text size={500}>Loading Analytics Dashboard...</Text>
                <Text size={300} style={{ color: '#605e5c' }}>
                    Analyzing consultation data and performance metrics
                </Text>
            </div>
        );
    }

    return (
        <div style={{ padding: '0' }}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px',
                }}
            >
                <div>
                    <Text size={800} weight="bold">
                        📊 Analytics Dashboard
                    </Text>
                    <Text size={300} style={{ color: '#605e5c', display: 'block' }}>
                        Last updated: {lastUpdated}
                    </Text>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <Dropdown
                        value={timeframe}
                        onOptionSelect={(_, data) =>
                            data.optionValue && handleTimeframeChange(data.optionValue)
                        }
                    >
                        {timeframeOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Dropdown>
                    <Button icon={<Fire24Regular />} onClick={handleRefresh} disabled={refreshing}>
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </Button>
                    <Button icon={<ArrowDownload24Regular />} onClick={() => handleExport('csv')}>
                        Export CSV
                    </Button>
                </div>
            </div>

            {/* Navigation Tabs */}
            <TabList
                selectedValue={activeTab}
                onTabSelect={(event, data) => setActiveTab(data.value as string)}
                style={{ marginBottom: '24px' }}
            >
                <Tab value="overview" icon={<ChartMultiple24Regular />}>
                    Overview
                </Tab>
                <Tab value="doctors" icon={<People24Regular />}>
                    Doctor Performance
                </Tab>
                <Tab value="trends" icon={<DataTrending24Regular />}>
                    Trends & Distribution
                </Tab>
            </TabList>

            {/* Tab Content */}
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'doctors' && renderDoctorsTab()}
            {activeTab === 'trends' && renderTrendsTab()}
        </div>
    );
};
